import React, { startTransition } from "react";
import { UseFormReturn } from "react-hook-form";
import { ClienteFormValues } from "../ClienteFormSchema";
import { FormattedTextField } from "@/components/form/FormattedTextField";
import { SelectField } from "@/components/form/SelectField";
import {
  PipelineFormSection,
  pipelineFieldGridClass,
  usePipelineForm,
} from "@/components/form/PipelineFormContext";
import { useCep } from "@/features/forms/hooks/useCep";
import { toast } from "sonner";

interface AddressStepProps {
  form: UseFormReturn<ClienteFormValues>;
}

const estadosNomes: { [key: string]: string } = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};

const estadosOptions = Object.entries(estadosNomes).map(([value, label]) => ({
  value: value.toUpperCase(),
  label,
}));

export function AddressStep({ form }: AddressStepProps) {
  const { consultarCep } = useCep();
  const { isPipeline } = usePipelineForm();
  const gridClass = pipelineFieldGridClass(isPipeline);

  const handleCepBlur = async () => {
    const cep = form.getValues("cep")?.replace(/\D/g, "");

    if (!cep || cep.length !== 8) return;

    try {
      const endereco = await consultarCep(cep);

      if (!endereco) {
        toast.error("CEP não encontrado. Por favor, verifique se o CEP está correto.");
        return;
      }

      startTransition(() => {
        form.setValue("logradouro", "");
        form.setValue("bairro", "");
        form.setValue("cidade", "");
        form.setValue("estado", "");
        form.setValue("complemento", "");

        if (endereco.logradouro) {
          form.setValue("logradouro", endereco.logradouro, { shouldValidate: true });
        }
        if (endereco.bairro) {
          form.setValue("bairro", endereco.bairro, { shouldValidate: true });
        }
        if (endereco.localidade) {
          form.setValue("cidade", endereco.localidade, { shouldValidate: true });
        }
        if (endereco.complemento) {
          form.setValue("complemento", endereco.complemento, { shouldValidate: true });
        }
        if (endereco.uf) {
          form.setValue("estado", endereco.uf.toUpperCase(), { shouldValidate: true });
        }
      });

      setTimeout(() => {
        form.trigger(["logradouro", "bairro", "cidade", "estado"]);
      }, 0);
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      toast.error("Erro ao buscar CEP. Por favor, tente novamente.");
    }
  };

  return (
    <PipelineFormSection>
      <div className={gridClass}>
        <FormattedTextField
          form={form}
          name="cep"
          label="CEP"
          formatter="cep"
          placeholder="00000-000"
          onBlur={handleCepBlur}
        />

        <FormattedTextField
          form={form}
          name="logradouro"
          label="Logradouro"
          formatter="texto"
          placeholder="Rua, Avenida, etc."
        />

        <FormattedTextField
          form={form}
          name="numero"
          label="Número"
          formatter="texto"
          placeholder="123"
        />

        <FormattedTextField
          form={form}
          name="complemento"
          label="Complemento"
          formatter="texto"
          placeholder="Apartamento, bloco, etc."
        />

        <FormattedTextField
          form={form}
          name="bairro"
          label="Bairro"
          formatter="texto"
          placeholder="Nome do bairro"
        />

        <FormattedTextField
          form={form}
          name="cidade"
          label="Cidade"
          formatter="texto"
          placeholder="Nome da cidade"
        />

        <SelectField
          form={form}
          name="estado"
          label="Estado"
          options={estadosOptions}
          placeholder="Selecione"
          showSearch
        />
      </div>
    </PipelineFormSection>
  );
}
